const t = {
  nav: {
    logoText: "The Coding Explorer",
    links: {
      about: "About",
      skills: "Skills",
      experience: "Experience",
      projects: "Projects",
      education: "Education",
      contact: "Contact",
      learning: "Learning",
      reflections: "Reflections",
      resources: "Resources",
    },
    drawer: {
      home: "Home",
      learning: "Learning",
      reflections: "Reflections",
      frontEndMaterials: "Front-End Materials",
      backEndMaterials: "Back-End Materials",
      thingsLearned: "Things I've Learned",
      resources: "Resources",
      myToolkit: "My Toolkit",
      comingSoon: "Soon",
    },
    ariaLabels: {
      openMenu: "Open navigation menu",
      closeMenu: "Close navigation menu",
      toggleColorMode: "Toggle color mode",
    },
  },

  hero: {
    greeting: "Hi, I'm",
    name: "Ahmad Basraa",
    roles: [
      "Software Engineer",
      "React Native Developer",
      "Frontend Developer",
      "Problem Solver",
    ],
    description:
      "I build reliable, user-focused applications with modern web technologies. Currently engineering mobile solutions at DailyPay.",
    primaryButton: "View My Work",
    secondaryButton: "Get In Touch",
  },

  about: {
    heading: "About Me",
    description:
      "I'm a Software Engineer with a BSc in Computer Science from Queen's University Belfast and over 3 years of professional experience building production applications. At DailyPay, I develop and maintain React Native mobile applications, focusing on code quality, comprehensive testing, and CI/CD best practices. I thrive on solving complex problems and writing clean, maintainable code that makes a real impact.",
  },

  education: {
    heading: "Education",
    entries: [
      {
        institution: "Queen's University Belfast",
        degree: "BSc Computer Science",
        duration: "2018 — 2022",
        description:
          "Studied core computer science fundamentals including algorithms, data structures, software engineering, and databases. Completed a final-year project implementing Bluestein's Algorithm for image analysis using C++ and Python.",
        highlights: [
          "Algorithms & Data Structures",
          "Software Engineering",
          "Database Systems",
          "Object-Oriented Programming",
          "Computer Architecture",
          "Final Year Project",
        ],
      },
    ],
  },

  skills: {
    heading: "Technical Skills",
    categories: [
      {
        title: "Languages",
        items: ["JavaScript", "TypeScript", "Java", "C++", "Python", "SQL"],
      },
      {
        title: "Frontend",
        items: [
          "React",
          "React Native",
          "HTML / CSS",
          "Chakra UI",
          "Framer Motion",
        ],
      },
      {
        title: "Tools & Platforms",
        items: [
          "Git",
          "GitHub",
          "GitLab",
          "NPM",
          "CI/CD Pipelines",
          "Jest",
          "Unity",
        ],
      },
      {
        title: "Practices",
        items: [
          "Code Reviews",
          "Unit Testing",
          "Agile / Scrum",
          "Pair Programming",
          "Responsive Design",
        ],
      },
    ],
  },

  experience: {
    heading: "Experience",
    data: [
      {
        company: "DailyPay",
        position: "Software Engineer",
        duration: "Aug 2022 — Present",
        image: "/assets/logo512.png",
        badges: [
          { name: "React Native", colorScheme: "teal" },
          { name: "TypeScript", colorScheme: "cyan" },
          { name: "JavaScript", colorScheme: "yellow" },
          { name: "Java", colorScheme: "orange" },
          { name: "CI/CD", colorScheme: "green" },
          { name: "Jest", colorScheme: "red" },
        ],
        listItems: [
          "Build and maintain a React Native mobile application serving thousands of users across iOS and Android",
          "Develop comprehensive unit and integration test suites, improving code reliability and reducing regressions",
          "Lead code review practices to enforce quality standards and promote clean, maintainable code",
          "Manage dependency updates and package development through NPM, streamlining team workflows",
          "Contribute to CI/CD pipeline improvements using Git-based workflows with GitHub and GitLab",
        ],
      },
      {
        company: "Sanguinius Ltd",
        position: "Software Support",
        duration: "Feb 2018 — Mar 2022",
        image: "/assets/Star.png",
        badges: [
          { name: "SQL", colorScheme: "green" },
          { name: "Database Management", colorScheme: "orange" },
          { name: "Automation", colorScheme: "red" },
          { name: "Data Analysis", colorScheme: "cyan" },
        ],
        listItems: [
          "Maintained and optimized SQL databases for product inventory, pricing, and sales data",
          "Created automated reporting scripts using SQL, reducing manual data processing time",
          "Provided technical support for retail software, troubleshooting system errors and data inconsistencies",
          "Documented database management workflows and best practices for the team",
        ],
      },
      {
        company: "Sensata Technologies",
        position: "Software Engineer Shadow",
        duration: "Aug — Sep 2018",
        image: "/assets/Sensata.png",
        badges: [
          { name: "Pair Programming", colorScheme: "yellow" },
          { name: "Agile / Scrum", colorScheme: "teal" },
          { name: "Problem Solving", colorScheme: "orange" },
        ],
        listItems: [
          "Shadowed senior engineers to learn industry development workflows including code planning, review, and deployment",
          "Gained hands-on experience with Agile methodologies (Scrum, Kanban) and pair programming practices",
          "Developed structured approaches to breaking down complex technical challenges",
        ],
      },
    ],
  },

  projects: {
    heading: "Projects",
    githubButton: "GitHub",
    data: [
      {
        title: "Bluestein's Algorithm — Image Analysis",
        subtitle: "Queen's University Belfast · Final Year Project",
        image: "/assets/Queens.png",
        githubLink: "https://github.com/ahmadbasraa818/final_qub_project",
        badges: [
          { name: "C++", colorScheme: "blue" },
          { name: "Python", colorScheme: "teal" },
          { name: "OpenCV", colorScheme: "red" },
          { name: "FFT", colorScheme: "yellow" },
          { name: "Matplotlib", colorScheme: "orange" },
        ],
        listItems: [
          "Implemented Bluestein's Algorithm in C++ to perform FFT analysis on non-power-of-two data for image blurriness detection",
          "Built a Python GUI for drag-and-drop image processing with automated FFT analysis pipeline",
          "Created visualization tools using Matplotlib for frequency magnitude and blurriness heatmaps",
        ],
      },
      {
        title: "Portfolio Website",
        subtitle: "Personal Project · React",
        image: "/assets/code.png",
        githubLink: "https://github.com/ahmadbasraa818/thecodingexplorer",
        badges: [
          { name: "React", colorScheme: "teal" },
          { name: "Chakra UI", colorScheme: "blue" },
          { name: "Framer Motion", colorScheme: "purple" },
          { name: "Jest", colorScheme: "red" },
        ],
        listItems: [
          "Built a responsive portfolio with React, Chakra UI, and Framer Motion animations",
          "Implemented dark/light theme support, smooth scroll navigation, and mobile-first design",
          "Set up comprehensive testing with Jest and React Testing Library",
        ],
      },
      {
        title: "Java Snake Game",
        subtitle: "Personal Project · Java",
        image: "/assets/Snake.png",
        githubLink: "https://github.com/ahmadbasraa818/SnakeGame",
        badges: [
          { name: "Java", colorScheme: "orange" },
          { name: "Game Logic", colorScheme: "cyan" },
          { name: "OOP", colorScheme: "green" },
        ],
        listItems: [
          "Designed and built a classic Snake game with increasing difficulty and real-time scoring",
          "Implemented collision detection, smooth gameplay mechanics, and responsive controls",
          "Structured with clean, modular architecture for maintainability and future expansion",
        ],
      },
    ],
  },

  contact: {
    heading: "Get In Touch",
    description:
      "I'm always open to discussing new opportunities, interesting projects, or just connecting with fellow developers.",
    linkedinLabel: "LinkedIn",
    githubLabel: "GitHub",
    emailLabel: "Email",
  },

  footer: {
    copyright: "Ahmad Basraa · The Coding Explorer",
    builtWith: "Built with React & Chakra UI",
  },

  profile: {
    name: "Ahmad Basraa",
    linkedin: "https://www.linkedin.com/in/ahmad-basraa-b1b002206/",
    github: "https://github.com/ahmadbasraa818",
    email: "",
  },

  frontEndLanding: {
    heading: "Front-End Learning",
    description:
      "A structured collection of front-end topics. Each section will be filled with guides, examples, and exercises. Select a topic to explore.",
    viewTopicButton: "View topic",
    pageTitle: "Front-End Learning | The Coding Explorer",
    badgeOverflowSuffix: "more",
  },

  frontEndTopic: {
    backButton: "All Topics",
    comingSoonNotice:
      "Full content for this topic is coming soon. Below is an outline of what will be covered.",
    whatYoullLearnHeading: "What you'll learn",
    technologiesHeading: "Technologies covered",
    pageTitleSuffix: "| The Coding Explorer",
  },

  frontEndTopics: [
    {
      company: "HTML & CSS Fundamentals",
      position: "The Building Blocks of the Web",
      duration: "Beginner",
      image: "/assets/code.png",
      tags: "Frontend",
      slug: "html-css-fundamentals",
      badges: [
        { name: "HTML5", colorScheme: "orange" },
        { name: "CSS3", colorScheme: "blue" },
        { name: "Semantic HTML", colorScheme: "green" },
        { name: "Flexbox", colorScheme: "teal" },
        { name: "CSS Grid", colorScheme: "purple" },
        { name: "Responsive Design", colorScheme: "red" },
      ],
      listItems: [
        "Coming soon: Structuring web pages with semantic HTML5 elements for accessibility and SEO.",
        "Coming soon: Styling with CSS — selectors, specificity, the cascade, and the box model.",
        "Coming soon: Building responsive layouts with Flexbox and CSS Grid.",
        "Coming soon: CSS variables and maintaining a consistent design system.",
        "Coming soon: Animations and transitions to bring interfaces to life.",
      ],
    },
    {
      company: "JavaScript Essentials",
      position: "Core Language Concepts Every Developer Needs",
      duration: "Beginner – Intermediate",
      image: "/assets/code.png",
      tags: "Frontend",
      slug: "javascript-essentials",
      badges: [
        { name: "JavaScript", colorScheme: "yellow" },
        { name: "ES6+", colorScheme: "orange" },
        { name: "DOM Manipulation", colorScheme: "blue" },
        { name: "Async/Await", colorScheme: "teal" },
        { name: "Promises", colorScheme: "green" },
        { name: "Event Handling", colorScheme: "red" },
      ],
      listItems: [
        "Coming soon: Variables, data types, functions, and control flow in modern JavaScript (ES6+).",
        "Coming soon: Working with the DOM — selecting, creating, and manipulating elements.",
        "Coming soon: Asynchronous JavaScript — callbacks, Promises, and async/await.",
        "Coming soon: Event-driven programming and handling user interactions.",
        "Coming soon: Modules, imports/exports, and structuring larger codebases.",
      ],
    },
    {
      company: "React Fundamentals",
      position: "Building UIs with the World's Most Popular Library",
      duration: "Intermediate",
      image: "/assets/code.png",
      tags: "Frontend",
      slug: "react-fundamentals",
      badges: [
        { name: "React", colorScheme: "teal" },
        { name: "JSX", colorScheme: "blue" },
        { name: "Hooks", colorScheme: "purple" },
        { name: "Components", colorScheme: "green" },
        { name: "Props & State", colorScheme: "orange" },
        { name: "React Router", colorScheme: "red" },
      ],
      listItems: [
        "Coming soon: Understanding the component model — functional components, props, and state.",
        "Coming soon: Core hooks: useState, useEffect, useContext, useRef, and writing custom hooks.",
        "Coming soon: Managing side effects and the React component lifecycle.",
        "Coming soon: Client-side routing with React Router.",
        "Coming soon: Performance optimisation — useMemo, useCallback, and React.memo.",
      ],
    },
    {
      company: "TypeScript for Frontend",
      position: "Adding Type Safety to Your JavaScript Projects",
      duration: "Intermediate",
      image: "/assets/code.png",
      tags: "Frontend",
      slug: "typescript-for-frontend",
      badges: [
        { name: "TypeScript", colorScheme: "blue" },
        { name: "Type Safety", colorScheme: "green" },
        { name: "Interfaces", colorScheme: "teal" },
        { name: "Generics", colorScheme: "purple" },
        { name: "React + TS", colorScheme: "orange" },
        { name: "Tooling", colorScheme: "gray" },
      ],
      listItems: [
        "Coming soon: TypeScript basics — types, interfaces, enums, and type inference.",
        "Coming soon: Typing React components, props, state, and event handlers.",
        "Coming soon: Generics for reusable, type-safe utilities and components.",
        "Coming soon: Integrating TypeScript into an existing React project.",
        "Coming soon: Common patterns and best practices for large TypeScript codebases.",
      ],
    },
    {
      company: "CSS Frameworks & Styling Libraries",
      position: "Accelerating UI Development with Proven Tools",
      duration: "Beginner – Intermediate",
      image: "/assets/code.png",
      tags: "Frontend",
      slug: "css-frameworks-styling-libraries",
      badges: [
        { name: "Tailwind CSS", colorScheme: "teal" },
        { name: "Bootstrap", colorScheme: "purple" },
        { name: "Chakra UI", colorScheme: "blue" },
        { name: "Styled Components", colorScheme: "pink" },
        { name: "CSS Modules", colorScheme: "green" },
        { name: "Emotion", colorScheme: "orange" },
      ],
      listItems: [
        "Coming soon: Utility-first CSS with Tailwind — building UIs without leaving your HTML.",
        "Coming soon: Bootstrap grid and component library for rapid prototyping.",
        "Coming soon: Component-driven styling with Chakra UI and its theming system.",
        "Coming soon: CSS-in-JS approaches — Styled Components and Emotion.",
        "Coming soon: Choosing the right styling strategy for your project.",
      ],
    },
    {
      company: "State Management",
      position: "Keeping Application Data Organised at Scale",
      duration: "Intermediate – Advanced",
      image: "/assets/code.png",
      tags: "Frontend",
      slug: "state-management",
      badges: [
        { name: "Redux Toolkit", colorScheme: "purple" },
        { name: "Context API", colorScheme: "blue" },
        { name: "Zustand", colorScheme: "green" },
        { name: "React Query", colorScheme: "orange" },
        { name: "Recoil", colorScheme: "teal" },
        { name: "MobX", colorScheme: "red" },
      ],
      listItems: [
        "Coming soon: React's built-in Context API for lightweight global state.",
        "Coming soon: Redux Toolkit — slices, reducers, thunks, and the Redux DevTools.",
        "Coming soon: Zustand as a lightweight alternative to Redux.",
        "Coming soon: Server state management with React Query (TanStack Query).",
        "Coming soon: Patterns for scalable state architecture and when to use each tool.",
      ],
    },
  ],

  backEndLanding: {
    heading: "Back-End Learning",
    description:
      "A structured collection of back-end topics. Each section will be filled with guides, examples, and exercises. Select a topic to explore.",
    viewTopicButton: "View topic",
    pageTitle: "Back-End Learning | The Coding Explorer",
    badgeOverflowSuffix: "more",
  },

  backEndTopic: {
    backButton: "All Topics",
    comingSoonNotice:
      "Full content for this topic is coming soon. Below is an outline of what will be covered.",
    whatYoullLearnHeading: "What you'll learn",
    technologiesHeading: "Technologies covered",
    pageTitleSuffix: "| The Coding Explorer",
  },

  backEndTopics: [
    {
      company: "Node.js & Express",
      position: "Building Server-Side Applications with JavaScript",
      duration: "Beginner – Intermediate",
      image: "/assets/code.png",
      tags: "Backend",
      slug: "nodejs-express",
      badges: [
        { name: "Node.js", colorScheme: "green" },
        { name: "Express", colorScheme: "gray" },
        { name: "REST APIs", colorScheme: "blue" },
        { name: "Middleware", colorScheme: "orange" },
        { name: "NPM", colorScheme: "red" },
        { name: "Async I/O", colorScheme: "teal" },
      ],
      listItems: [
        "Coming soon: Setting up a Node.js project and understanding the event loop.",
        "Coming soon: Building RESTful APIs with Express — routing, middleware, and error handling.",
        "Coming soon: Working with request/response objects, query params, and body parsing.",
        "Coming soon: Structuring a Node.js project for maintainability and scalability.",
        "Coming soon: Environment variables, configuration, and deployment basics.",
      ],
    },
    {
      company: "Databases — SQL & NoSQL",
      position: "Storing and Querying Data Effectively",
      duration: "Beginner – Intermediate",
      image: "/assets/code.png",
      tags: "Backend",
      slug: "databases-sql-nosql",
      badges: [
        { name: "SQL", colorScheme: "blue" },
        { name: "PostgreSQL", colorScheme: "cyan" },
        { name: "MongoDB", colorScheme: "green" },
        { name: "ORMs", colorScheme: "purple" },
        { name: "Data Modelling", colorScheme: "orange" },
        { name: "Migrations", colorScheme: "red" },
      ],
      listItems: [
        "Coming soon: Relational databases — tables, joins, indexes, and SQL fundamentals.",
        "Coming soon: NoSQL databases — document stores, key-value stores, and when to use them.",
        "Coming soon: Data modelling patterns for relational and document databases.",
        "Coming soon: Using ORMs (Sequelize, Prisma) and ODMs (Mongoose) for database access.",
        "Coming soon: Database migrations, seeding, and managing schema changes over time.",
      ],
    },
    {
      company: "REST API Design",
      position: "Designing Clean, Scalable APIs",
      duration: "Intermediate",
      image: "/assets/code.png",
      tags: "Backend",
      slug: "rest-api-design",
      badges: [
        { name: "REST", colorScheme: "blue" },
        { name: "HTTP Methods", colorScheme: "green" },
        { name: "Status Codes", colorScheme: "orange" },
        { name: "Versioning", colorScheme: "purple" },
        { name: "Pagination", colorScheme: "teal" },
        { name: "Documentation", colorScheme: "gray" },
      ],
      listItems: [
        "Coming soon: REST principles — resources, HTTP methods, and status codes.",
        "Coming soon: Designing consistent URL structures and naming conventions.",
        "Coming soon: Pagination, filtering, sorting, and search patterns.",
        "Coming soon: API versioning strategies and backwards compatibility.",
        "Coming soon: Documenting APIs with OpenAPI/Swagger.",
      ],
    },
    {
      company: "Authentication & Security",
      position: "Securing Your Applications and Protecting User Data",
      duration: "Intermediate",
      image: "/assets/code.png",
      tags: "Backend",
      slug: "authentication-security",
      badges: [
        { name: "JWT", colorScheme: "orange" },
        { name: "OAuth 2.0", colorScheme: "blue" },
        { name: "Bcrypt", colorScheme: "red" },
        { name: "CORS", colorScheme: "teal" },
        { name: "HTTPS", colorScheme: "green" },
        { name: "OWASP", colorScheme: "purple" },
      ],
      listItems: [
        "Coming soon: Password hashing with bcrypt and secure storage practices.",
        "Coming soon: Token-based authentication with JWTs — access tokens and refresh tokens.",
        "Coming soon: OAuth 2.0 flows and integrating third-party login providers.",
        "Coming soon: CORS configuration, CSRF protection, and common security headers.",
        "Coming soon: OWASP Top 10 — understanding and preventing common vulnerabilities.",
      ],
    },
    {
      company: "Server Architecture",
      position: "Patterns for Scalable Back-End Systems",
      duration: "Intermediate – Advanced",
      image: "/assets/code.png",
      tags: "Backend",
      slug: "server-architecture",
      badges: [
        { name: "MVC", colorScheme: "blue" },
        { name: "Microservices", colorScheme: "purple" },
        { name: "Message Queues", colorScheme: "orange" },
        { name: "Caching", colorScheme: "teal" },
        { name: "Load Balancing", colorScheme: "green" },
        { name: "WebSockets", colorScheme: "red" },
      ],
      listItems: [
        "Coming soon: MVC and layered architecture for organising server-side code.",
        "Coming soon: Monolith vs microservices — trade-offs and when to choose each.",
        "Coming soon: Caching strategies with Redis for performance optimisation.",
        "Coming soon: Message queues and event-driven architecture basics.",
        "Coming soon: Real-time communication with WebSockets and Server-Sent Events.",
      ],
    },
    {
      company: "DevOps Basics",
      position: "Deployment, CI/CD, and Cloud Services",
      duration: "Intermediate",
      image: "/assets/code.png",
      tags: "Backend",
      slug: "devops-basics",
      badges: [
        { name: "Docker", colorScheme: "blue" },
        { name: "CI/CD", colorScheme: "green" },
        { name: "AWS", colorScheme: "orange" },
        { name: "Linux", colorScheme: "gray" },
        { name: "Nginx", colorScheme: "teal" },
        { name: "GitHub Actions", colorScheme: "purple" },
      ],
      listItems: [
        "Coming soon: Containerising applications with Docker and Docker Compose.",
        "Coming soon: Setting up CI/CD pipelines with GitHub Actions or GitLab CI.",
        "Coming soon: Cloud deployment basics — AWS, Heroku, and Vercel.",
        "Coming soon: Reverse proxies and process managers (Nginx, PM2).",
        "Coming soon: Logging, monitoring, and debugging production applications.",
      ],
    },
  ],

  testingLanding: {
    heading: "Testing & QA Learning",
    description:
      "A structured collection of testing and quality assurance topics. From unit tests to end-to-end testing, learn how to build confidence in your code. Select a topic to explore.",
    viewTopicButton: "View topic",
    pageTitle: "Testing & QA | The Coding Explorer",
    badgeOverflowSuffix: "more",
  },

  testingTopic: {
    backButton: "All Topics",
    comingSoonNotice:
      "Full content for this topic is coming soon. Below is an outline of what will be covered.",
    whatYoullLearnHeading: "What you'll learn",
    technologiesHeading: "Technologies covered",
    pageTitleSuffix: "| The Coding Explorer",
  },

  testingTopics: [
    {
      company: "Unit Testing Fundamentals",
      position: "Writing Reliable Tests for Individual Functions and Components",
      duration: "Beginner",
      image: "/assets/code.png",
      tags: "Testing",
      slug: "unit-testing-fundamentals",
      badges: [
        { name: "Jest", colorScheme: "red" },
        { name: "Assertions", colorScheme: "blue" },
        { name: "Mocking", colorScheme: "purple" },
        { name: "Test Runners", colorScheme: "green" },
        { name: "Code Coverage", colorScheme: "orange" },
        { name: "TDD", colorScheme: "teal" },
      ],
      listItems: [
        "Coming soon: Understanding the testing pyramid and where unit tests fit.",
        "Coming soon: Writing your first tests with Jest — describe, it, expect.",
        "Coming soon: Mocking dependencies — jest.fn(), jest.mock(), and manual mocks.",
        "Coming soon: Test-driven development (TDD) workflow and benefits.",
        "Coming soon: Measuring and interpreting code coverage reports.",
      ],
    },
    {
      company: "React Testing Library",
      position: "Testing React Components the Way Users Interact with Them",
      duration: "Intermediate",
      image: "/assets/code.png",
      tags: "Testing",
      slug: "react-testing-library",
      badges: [
        { name: "React Testing Library", colorScheme: "teal" },
        { name: "Jest", colorScheme: "red" },
        { name: "User Events", colorScheme: "blue" },
        { name: "Queries", colorScheme: "green" },
        { name: "Accessibility", colorScheme: "purple" },
        { name: "Async Testing", colorScheme: "orange" },
      ],
      listItems: [
        "Coming soon: Philosophy of testing — testing behaviour, not implementation details.",
        "Coming soon: Queries — getByRole, getByText, findBy, and queryBy variants.",
        "Coming soon: Simulating user interactions with fireEvent and userEvent.",
        "Coming soon: Testing asynchronous components — waitFor, findBy, and act.",
        "Coming soon: Testing forms, modals, and navigation in React apps.",
      ],
    },
    {
      company: "Integration Testing",
      position: "Verifying That Components Work Together Correctly",
      duration: "Intermediate",
      image: "/assets/code.png",
      tags: "Testing",
      slug: "integration-testing",
      badges: [
        { name: "API Mocking", colorScheme: "blue" },
        { name: "MSW", colorScheme: "orange" },
        { name: "Test Fixtures", colorScheme: "green" },
        { name: "Database Testing", colorScheme: "purple" },
        { name: "Supertest", colorScheme: "teal" },
        { name: "Test Isolation", colorScheme: "red" },
      ],
      listItems: [
        "Coming soon: Distinguishing integration tests from unit and end-to-end tests.",
        "Coming soon: Mocking API calls with Mock Service Worker (MSW).",
        "Coming soon: Testing API routes with Supertest and in-memory databases.",
        "Coming soon: Setting up test fixtures and managing test data.",
        "Coming soon: Ensuring test isolation and avoiding flaky tests.",
      ],
    },
    {
      company: "End-to-End Testing",
      position: "Automating Full User Workflows in the Browser",
      duration: "Intermediate – Advanced",
      image: "/assets/code.png",
      tags: "Testing",
      slug: "e2e-testing",
      badges: [
        { name: "Cypress", colorScheme: "green" },
        { name: "Playwright", colorScheme: "purple" },
        { name: "Browser Automation", colorScheme: "blue" },
        { name: "Selectors", colorScheme: "orange" },
        { name: "CI Integration", colorScheme: "teal" },
        { name: "Visual Regression", colorScheme: "red" },
      ],
      listItems: [
        "Coming soon: Setting up Cypress or Playwright for browser-based testing.",
        "Coming soon: Writing reliable selectors and avoiding brittle tests.",
        "Coming soon: Testing complete user flows — login, navigation, form submission.",
        "Coming soon: Running E2E tests in CI/CD pipelines.",
        "Coming soon: Visual regression testing with screenshot comparison.",
      ],
    },
    {
      company: "Mobile App Testing",
      position: "Testing React Native Applications Across Platforms",
      duration: "Intermediate",
      image: "/assets/code.png",
      tags: "Testing",
      slug: "mobile-app-testing",
      badges: [
        { name: "React Native Testing Library", colorScheme: "teal" },
        { name: "Detox", colorScheme: "blue" },
        { name: "Jest", colorScheme: "red" },
        { name: "Platform Testing", colorScheme: "green" },
        { name: "Snapshot Testing", colorScheme: "purple" },
        { name: "Mocking Native Modules", colorScheme: "orange" },
      ],
      listItems: [
        "Coming soon: Unit testing React Native components with RNTL and Jest.",
        "Coming soon: Mocking native modules and platform-specific code.",
        "Coming soon: Snapshot testing for detecting unintended UI changes.",
        "Coming soon: End-to-end mobile testing with Detox.",
        "Coming soon: Testing across iOS and Android platforms.",
      ],
    },
    {
      company: "Test Strategy & Best Practices",
      position: "Building a Testing Culture That Scales",
      duration: "Intermediate – Advanced",
      image: "/assets/code.png",
      tags: "Testing",
      slug: "test-strategy-best-practices",
      badges: [
        { name: "Test Pyramid", colorScheme: "blue" },
        { name: "CI/CD", colorScheme: "green" },
        { name: "Code Review", colorScheme: "purple" },
        { name: "Flaky Tests", colorScheme: "red" },
        { name: "Test Maintenance", colorScheme: "orange" },
        { name: "Quality Metrics", colorScheme: "teal" },
      ],
      listItems: [
        "Coming soon: Designing a test strategy — what to test and at which level.",
        "Coming soon: Balancing speed and coverage in your test suite.",
        "Coming soon: Identifying and fixing flaky tests.",
        "Coming soon: Integrating testing into code review and CI/CD workflows.",
        "Coming soon: Maintaining a healthy test suite as the codebase grows.",
      ],
    },
  ],

  mobileLanding: {
    heading: "Mobile Development Learning",
    description:
      "A structured collection of mobile development topics focused on React Native and cross-platform app development. Select a topic to explore.",
    viewTopicButton: "View topic",
    pageTitle: "Mobile Development | The Coding Explorer",
    badgeOverflowSuffix: "more",
  },

  mobileTopic: {
    backButton: "All Topics",
    comingSoonNotice:
      "Full content for this topic is coming soon. Below is an outline of what will be covered.",
    whatYoullLearnHeading: "What you'll learn",
    technologiesHeading: "Technologies covered",
    pageTitleSuffix: "| The Coding Explorer",
  },

  mobileTopics: [
    {
      company: "React Native Fundamentals",
      position: "Building Cross-Platform Mobile Apps with JavaScript",
      duration: "Beginner – Intermediate",
      image: "/assets/code.png",
      tags: "Mobile",
      slug: "react-native-fundamentals",
      badges: [
        { name: "React Native", colorScheme: "teal" },
        { name: "Expo", colorScheme: "blue" },
        { name: "Core Components", colorScheme: "green" },
        { name: "StyleSheet", colorScheme: "purple" },
        { name: "Navigation", colorScheme: "orange" },
        { name: "Platform APIs", colorScheme: "red" },
      ],
      listItems: [
        "Coming soon: Setting up a React Native development environment with Expo and bare CLI.",
        "Coming soon: Core components — View, Text, ScrollView, FlatList, and TouchableOpacity.",
        "Coming soon: Styling with StyleSheet, Flexbox, and platform-specific styles.",
        "Coming soon: Navigation with React Navigation — stacks, tabs, and drawers.",
        "Coming soon: Accessing device APIs — camera, location, and permissions.",
      ],
    },
    {
      company: "State Management in Mobile",
      position: "Managing Complex App State in React Native",
      duration: "Intermediate",
      image: "/assets/code.png",
      tags: "Mobile",
      slug: "mobile-state-management",
      badges: [
        { name: "Redux Toolkit", colorScheme: "purple" },
        { name: "Context API", colorScheme: "blue" },
        { name: "AsyncStorage", colorScheme: "green" },
        { name: "Zustand", colorScheme: "teal" },
        { name: "Persistence", colorScheme: "orange" },
        { name: "MMKV", colorScheme: "red" },
      ],
      listItems: [
        "Coming soon: Choosing between Context, Redux, and lightweight alternatives for mobile.",
        "Coming soon: Persisting state with AsyncStorage and react-native-mmkv.",
        "Coming soon: Handling offline-first data and sync strategies.",
        "Coming soon: Managing navigation state alongside application state.",
        "Coming soon: Performance considerations — avoiding unnecessary re-renders in lists.",
      ],
    },
    {
      company: "Mobile UI & Animations",
      position: "Creating Smooth, Native-Feeling Interfaces",
      duration: "Intermediate",
      image: "/assets/code.png",
      tags: "Mobile",
      slug: "mobile-ui-animations",
      badges: [
        { name: "Reanimated", colorScheme: "purple" },
        { name: "Gesture Handler", colorScheme: "blue" },
        { name: "Lottie", colorScheme: "orange" },
        { name: "Animated API", colorScheme: "teal" },
        { name: "Layout Animation", colorScheme: "green" },
        { name: "Shared Transitions", colorScheme: "red" },
      ],
      listItems: [
        "Coming soon: Understanding the Animated API and running animations on the UI thread.",
        "Coming soon: React Native Reanimated for performant, gesture-driven animations.",
        "Coming soon: Handling swipes, drags, and pinch gestures with Gesture Handler.",
        "Coming soon: Integrating Lottie animations for rich visual feedback.",
        "Coming soon: Layout animations and shared element transitions between screens.",
      ],
    },
    {
      company: "Native Modules & Platform Code",
      position: "Bridging JavaScript and Native iOS/Android Code",
      duration: "Advanced",
      image: "/assets/code.png",
      tags: "Mobile",
      slug: "native-modules",
      badges: [
        { name: "Native Modules", colorScheme: "blue" },
        { name: "Swift", colorScheme: "orange" },
        { name: "Kotlin", colorScheme: "purple" },
        { name: "Bridge", colorScheme: "teal" },
        { name: "Turbo Modules", colorScheme: "green" },
        { name: "New Architecture", colorScheme: "red" },
      ],
      listItems: [
        "Coming soon: Understanding the React Native bridge and how JS communicates with native code.",
        "Coming soon: Writing native modules in Swift (iOS) and Kotlin (Android).",
        "Coming soon: The New Architecture — Fabric and Turbo Modules.",
        "Coming soon: Linking native libraries and using community native packages.",
        "Coming soon: Debugging native crashes and platform-specific issues.",
      ],
    },
    {
      company: "App Performance Optimisation",
      position: "Making Your Mobile App Fast and Responsive",
      duration: "Intermediate – Advanced",
      image: "/assets/code.png",
      tags: "Mobile",
      slug: "mobile-performance",
      badges: [
        { name: "Profiling", colorScheme: "blue" },
        { name: "FlatList", colorScheme: "green" },
        { name: "Hermes", colorScheme: "orange" },
        { name: "Bundle Size", colorScheme: "purple" },
        { name: "Memory Leaks", colorScheme: "red" },
        { name: "Flipper", colorScheme: "teal" },
      ],
      listItems: [
        "Coming soon: Profiling with Flipper, React DevTools, and Xcode/Android Studio.",
        "Coming soon: Optimising lists with FlatList, getItemLayout, and windowing.",
        "Coming soon: Reducing bundle size and enabling Hermes for faster startup.",
        "Coming soon: Identifying and fixing memory leaks in React Native.",
        "Coming soon: Image optimisation and caching strategies for mobile.",
      ],
    },
    {
      company: "Publishing & Distribution",
      position: "Getting Your App Into Users' Hands",
      duration: "Intermediate",
      image: "/assets/code.png",
      tags: "Mobile",
      slug: "publishing-distribution",
      badges: [
        { name: "App Store", colorScheme: "blue" },
        { name: "Google Play", colorScheme: "green" },
        { name: "Code Signing", colorScheme: "orange" },
        { name: "Fastlane", colorScheme: "red" },
        { name: "OTA Updates", colorScheme: "purple" },
        { name: "CI/CD", colorScheme: "teal" },
      ],
      listItems: [
        "Coming soon: Code signing, provisioning profiles, and keystores explained.",
        "Coming soon: Submitting to the App Store and Google Play — guidelines and review process.",
        "Coming soon: Automating builds and releases with Fastlane.",
        "Coming soon: Over-the-air updates with CodePush and EAS Update.",
        "Coming soon: Setting up CI/CD for mobile with GitHub Actions or Bitrise.",
      ],
    },
  ],

  designLanding: {
    heading: "Software Design Learning",
    description:
      "A structured collection of software design and architecture topics. Learn the principles, patterns, and practices that separate good code from great code. Select a topic to explore.",
    viewTopicButton: "View topic",
    pageTitle: "Software Design | The Coding Explorer",
    badgeOverflowSuffix: "more",
  },

  designTopic: {
    backButton: "All Topics",
    comingSoonNotice:
      "Full content for this topic is coming soon. Below is an outline of what will be covered.",
    whatYoullLearnHeading: "What you'll learn",
    technologiesHeading: "Technologies covered",
    pageTitleSuffix: "| The Coding Explorer",
  },

  designTopics: [
    {
      company: "SOLID Principles",
      position: "Writing Flexible, Maintainable Object-Oriented Code",
      duration: "Intermediate",
      image: "/assets/code.png",
      tags: "Design",
      slug: "solid-principles",
      badges: [
        { name: "Single Responsibility", colorScheme: "blue" },
        { name: "Open/Closed", colorScheme: "green" },
        { name: "Liskov Substitution", colorScheme: "purple" },
        { name: "Interface Segregation", colorScheme: "orange" },
        { name: "Dependency Inversion", colorScheme: "teal" },
        { name: "OOP", colorScheme: "red" },
      ],
      listItems: [
        "Coming soon: Single Responsibility — why every class and function should have one reason to change.",
        "Coming soon: Open/Closed — designing modules that are open for extension, closed for modification.",
        "Coming soon: Liskov Substitution — ensuring subtypes are truly interchangeable with their base types.",
        "Coming soon: Interface Segregation — keeping interfaces small, focused, and client-specific.",
        "Coming soon: Dependency Inversion — depending on abstractions rather than concrete implementations.",
      ],
    },
    {
      company: "Design Patterns",
      position: "Proven Solutions to Common Software Problems",
      duration: "Intermediate – Advanced",
      image: "/assets/code.png",
      tags: "Design",
      slug: "design-patterns",
      badges: [
        { name: "Factory", colorScheme: "blue" },
        { name: "Observer", colorScheme: "green" },
        { name: "Strategy", colorScheme: "purple" },
        { name: "Singleton", colorScheme: "orange" },
        { name: "Decorator", colorScheme: "teal" },
        { name: "Adapter", colorScheme: "red" },
      ],
      listItems: [
        "Coming soon: Creational patterns — Factory, Builder, and Singleton in practice.",
        "Coming soon: Structural patterns — Adapter, Decorator, and Facade for flexible composition.",
        "Coming soon: Behavioural patterns — Observer, Strategy, and Command for clean logic flow.",
        "Coming soon: Recognising when a pattern fits and when it's overkill.",
        "Coming soon: Real-world examples of patterns in popular frameworks and libraries.",
      ],
    },
    {
      company: "Clean Code Practices",
      position: "Writing Code That Humans Can Read and Maintain",
      duration: "Beginner – Intermediate",
      image: "/assets/code.png",
      tags: "Design",
      slug: "clean-code",
      badges: [
        { name: "Naming", colorScheme: "blue" },
        { name: "Functions", colorScheme: "green" },
        { name: "Refactoring", colorScheme: "purple" },
        { name: "DRY", colorScheme: "orange" },
        { name: "KISS", colorScheme: "teal" },
        { name: "Code Smells", colorScheme: "red" },
      ],
      listItems: [
        "Coming soon: Meaningful naming — choosing names that reveal intent and reduce comments.",
        "Coming soon: Writing small, focused functions that do one thing well.",
        "Coming soon: Identifying and eliminating code smells — duplication, long methods, and god objects.",
        "Coming soon: The art of refactoring — improving code without changing behaviour.",
        "Coming soon: When DRY helps and when it hurts — avoiding premature abstraction.",
      ],
    },
    {
      company: "Architecture Patterns",
      position: "Structuring Applications for Scale and Maintainability",
      duration: "Intermediate – Advanced",
      image: "/assets/code.png",
      tags: "Design",
      slug: "architecture-patterns",
      badges: [
        { name: "MVC", colorScheme: "blue" },
        { name: "MVVM", colorScheme: "green" },
        { name: "Clean Architecture", colorScheme: "purple" },
        { name: "Layered", colorScheme: "orange" },
        { name: "Hexagonal", colorScheme: "teal" },
        { name: "Domain-Driven", colorScheme: "red" },
      ],
      listItems: [
        "Coming soon: MVC, MVP, and MVVM — understanding presentation layer patterns.",
        "Coming soon: Clean Architecture — separating business logic from frameworks and I/O.",
        "Coming soon: Layered architecture — organising code into controllers, services, and repositories.",
        "Coming soon: Hexagonal (Ports & Adapters) architecture for testable, framework-independent code.",
        "Coming soon: Choosing the right architecture for your project size and team.",
      ],
    },
    {
      company: "API & System Design",
      position: "Designing Systems That Scale and Communicate Well",
      duration: "Intermediate – Advanced",
      image: "/assets/code.png",
      tags: "Design",
      slug: "api-system-design",
      badges: [
        { name: "System Design", colorScheme: "blue" },
        { name: "API Contracts", colorScheme: "green" },
        { name: "Event-Driven", colorScheme: "purple" },
        { name: "Caching", colorScheme: "orange" },
        { name: "Scalability", colorScheme: "teal" },
        { name: "Trade-offs", colorScheme: "red" },
      ],
      listItems: [
        "Coming soon: Designing clear API contracts and versioning strategies.",
        "Coming soon: Event-driven architecture — decoupling services with events and queues.",
        "Coming soon: Caching layers — where to cache and how to invalidate.",
        "Coming soon: Horizontal vs vertical scaling — trade-offs and when to use each.",
        "Coming soon: Common system design interview patterns and how to approach them.",
      ],
    },
    {
      company: "Code Review & Collaboration Patterns",
      position: "Building Quality Through Team Practices",
      duration: "Beginner – Intermediate",
      image: "/assets/code.png",
      tags: "Design",
      slug: "code-review-collaboration",
      badges: [
        { name: "Code Review", colorScheme: "blue" },
        { name: "PR Best Practices", colorScheme: "green" },
        { name: "Linting", colorScheme: "purple" },
        { name: "Pair Programming", colorScheme: "orange" },
        { name: "Git Workflow", colorScheme: "teal" },
        { name: "Documentation", colorScheme: "red" },
      ],
      listItems: [
        "Coming soon: Writing pull requests that are easy to review — size, description, and context.",
        "Coming soon: Giving constructive code review feedback that improves the codebase and the team.",
        "Coming soon: Setting up linting and formatting for consistent code style.",
        "Coming soon: Git branching strategies — trunk-based, Git Flow, and feature branches.",
        "Coming soon: Writing useful documentation — READMEs, ADRs, and inline comments.",
      ],
    },
  ],

  learnings: {
    heading: "Things I've Learned",
    pageTitle: "Things I've Learned | The Coding Explorer",
    intro:
      "Over the years, I've picked up lessons that go beyond code. These are the habits, mindsets, and realisations that have shaped how I work and grow as a developer. This page is a living document — I'll keep adding to it as new experiences bring new insights.",
    readMore: "Read more",
    backButton: "Back",
    pageTitleSuffix: "| The Coding Explorer",
    categories: [
      {
        label: "Mindset",
        color: "purple",
        entries: [
          {
            slug: "progress-over-perfection",
            title: "Progress over perfection",
            body: "Early in my career, I spent too long trying to write the perfect solution on the first try. I've learned that shipping something solid and iterating is almost always better than waiting for perfect. Code reviews, refactoring, and continuous improvement exist for a reason.",
            expanded: "This lesson hit me hardest during my first months at DailyPay. I'd spend hours agonising over variable names and architecture before writing a single line of production code. Meanwhile, my teammates were shipping features, getting feedback, and improving iteratively.\n\nThe turning point was realising that 'done' beats 'perfect' almost every time. A working feature that's 80% polished teaches you more than a theoretical design that never ships. Code reviews exist precisely to catch what you missed. Refactoring sprints exist to clean up what you rushed.\n\nNow my approach is: get it working, get it reviewed, get it improved. Three passes that each take an hour will always beat one pass you spend a full day on. The first version doesn't need to be your last.",
          },
          {
            slug: "embrace-being-uncomfortable",
            title: "Embrace being uncomfortable",
            body: "Growth happens when you take on tasks you're not fully ready for. Volunteering for unfamiliar work — a new language, a different part of the stack — is one of the fastest ways to level up. The discomfort is temporary; the skills stick.",
            expanded: "I used to avoid picking up tickets that involved parts of the codebase I didn't understand. It felt safer to stay in my lane. But the engineers I admired most were the ones who'd say 'I haven't done that before, but I'll figure it out.'\n\nSo I started doing the same. I volunteered for tasks in areas I knew nothing about — CI/CD pipelines, native module debugging, database migration scripts. Every single time, the first day was uncomfortable. By the end of the week, I'd learned something I'd carry for years.\n\nThe secret is that everyone feels uncomfortable when learning something new. The difference is whether you lean into it or avoid it. The engineers who grow fastest are the ones who are comfortable being uncomfortable.",
          },
        ],
      },
      {
        label: "Technical",
        color: "teal",
        entries: [
          {
            slug: "tests-save-more-time",
            title: "Tests save more time than they cost",
            body: "Writing tests felt like extra work at first. But after catching countless regressions and being able to refactor with confidence, testing became one of the highest-leverage habits I've built. A good test suite is your safety net.",
            expanded: "When I first started writing tests, it felt like I was doubling my workload for no visible benefit. The feature already works — why write code to prove it?\n\nThen came the first time a test caught a regression I would have shipped to production. A small change in a utility function broke a completely unrelated feature, and the test suite flagged it in seconds. That one catch saved hours of debugging, a hotfix, and probably a few frustrated users.\n\nNow I write tests before I consider a feature done. Not because I enjoy it (though I've grown to), but because the confidence it gives me is worth every line. I can refactor aggressively, update dependencies fearlessly, and review PRs knowing the safety net is there. The ROI on testing is one of the highest in software engineering.",
          },
          {
            slug: "read-the-error-message",
            title: "Read the error message — all of it",
            body: "It sounds obvious, but so many debugging sessions drag on because the answer was already in the stack trace. Slowing down to read error messages carefully, line by line, has saved me hours.",
            expanded: "I can't count the number of times I've spent 30 minutes debugging something, only to realise the exact answer was in the first line of the error message. It's a habit that's embarrassingly hard to build — our instinct is to see red text and immediately start guessing.\n\nThe stack trace tells you the file, the line number, and usually the exact nature of the problem. 'Cannot read property X of undefined' tells you exactly what's null. 'Module not found' tells you exactly what's missing. 'Expected 2 arguments but got 1' tells you exactly what you forgot.\n\nI now force myself to read the entire error before touching the code. I copy it into a note, read it top to bottom, and only then start investigating. This single habit has probably saved me more cumulative time than any tool or framework I've learned.",
          },
        ],
      },
      {
        label: "Collaboration",
        color: "blue",
        entries: [
          {
            slug: "code-reviews-are-learning-opportunities",
            title: "Code reviews are learning opportunities",
            body: "Some of my biggest growth has come from reviewing others' code and having mine reviewed. It's not about finding faults — it's about sharing knowledge, catching blind spots, and building a codebase everyone can work in confidently.",
            expanded: "Early on, I dreaded code reviews. Having someone critique your work felt personal. But the more I participated — both as a reviewer and as the one being reviewed — the more I realised it's one of the most valuable learning mechanisms in engineering.\n\nAs a reviewer, you see how other people solve problems. You discover patterns you'd never have thought of. You learn to read code critically and articulate why something could be better. As the one being reviewed, you get a second pair of eyes that catches edge cases, suggests cleaner approaches, and shares context you might not have had.\n\nThe best code reviews I've been part of aren't about nitpicking semicolons. They're conversations about trade-offs, maintainability, and intent. A good review comment teaches both the author and the reviewer something new.",
          },
          {
            slug: "ask-questions-early",
            title: "Ask questions early",
            body: "Spending three hours stuck on something when a five-minute conversation with a teammate would have unblocked you isn't persistence — it's wasted time. Knowing when to ask is a skill worth developing.",
            expanded: "There's a pride thing that comes with wanting to figure everything out yourself. I used to think asking for help was a sign of weakness. It took me a while to realise the opposite is true — knowing when to ask is one of the strongest signals of a mature engineer.\n\nMy rule of thumb now: if I've been stuck for 30 minutes without making meaningful progress, I ask. Not 'please solve this for me,' but 'here's what I've tried, here's where I'm stuck, do you have any pointers?' That framing respects everyone's time and usually gets you unstuck in minutes.\n\nThe best engineers I've worked with ask questions constantly — not because they don't know things, but because they value their time and their team's time. There's no prize for suffering in silence.",
          },
        ],
      },
      {
        label: "Career",
        color: "orange",
        entries: [
          {
            slug: "your-first-role-shapes-your-habits",
            title: "Your first role shapes your habits",
            body: "The standards, practices, and culture of your first engineering role leave a lasting mark. Working in environments that value clean code, thorough testing, and continuous learning has shaped habits that carry through every project.",
            expanded: "Your first engineering job teaches you what 'normal' looks like. If your first team writes tests, you'll always write tests. If they do thorough code reviews, you'll expect that everywhere. If they cut corners, you might not realise those corners exist until much later.\n\nI was fortunate that my environment at DailyPay set a high bar from day one. Comprehensive testing, meaningful code reviews, clean commit histories, and CI/CD that actually catches things. These weren't optional — they were how the team worked.\n\nLooking back, those early habits became my baseline. When I work on personal projects or join a new team, I instinctively reach for the same standards. Your first role doesn't just teach you a tech stack — it teaches you how to be a professional engineer.",
          },
          {
            slug: "document-what-you-build",
            title: "Document what you build",
            body: "Keeping a record of your projects, contributions, and lessons learned makes performance reviews, interviews, and portfolio updates far easier. Future you will thank present you for writing it down.",
            expanded: "I started keeping a simple log of things I worked on — what I built, problems I solved, impact it had. Just a few bullet points each week. It felt unnecessary at the time, but it's paid for itself many times over.\n\nWhen performance review season comes around, I don't have to scramble to remember what I did six months ago. When I'm preparing for interviews, I have concrete examples ready. When I'm updating my portfolio, the content is already there.\n\nThe format doesn't matter — a text file, a Notion page, a markdown doc. What matters is the habit of writing it down while it's fresh. 'I'll remember this' is one of the biggest lies we tell ourselves. Write it down. Future you will be grateful.",
          },
        ],
      },
      {
        label: "Problem Solving",
        color: "red",
        entries: [
          {
            slug: "break-it-down",
            title: "Break it down before you build it up",
            body: "The biggest problems always look impossible at first glance. I've learned to break them into smaller, manageable pieces. Once you solve the small parts, the big picture assembles itself. This approach works in code and in life.",
            expanded: "My final-year project at university was the first time I faced a genuinely complex problem — implementing Bluestein's Algorithm for image analysis. The full scope was overwhelming. FFT maths, C++ implementation, Python GUI, visualisation tools. I didn't know where to start.\n\nA mentor told me: 'You don't need to solve the whole thing. Solve the smallest piece that moves you forward.' So I started with the maths on paper. Then a basic C++ function. Then a test case. Then another. Each tiny step made the next one clearer.\n\nI use this approach on every complex task now. Big feature? Break it into tickets. Confusing bug? Isolate the smallest reproducible case. Overwhelming refactor? Start with one file. The skill isn't solving big problems — it's turning big problems into small ones.",
          },
          {
            slug: "rubber-duck-debugging",
            title: "Rubber duck debugging is real",
            body: "Explaining a problem out loud — to a colleague, a rubber duck, or even yourself — forces you to articulate what you actually know and where the gaps are. More often than not, the answer becomes obvious mid-explanation.",
            expanded: "I used to think rubber duck debugging was a joke — something people mentioned but nobody actually did. Then I started doing it out of desperation during a particularly stubborn bug, and the answer came to me mid-sentence.\n\nThe magic isn't in the rubber duck. It's in the act of structuring your thoughts linearly. When you're debugging in your head, your thinking jumps around — 'maybe it's this, no wait, could be that.' When you explain it out loud, you're forced to go step by step: 'The function receives X, transforms it into Y, passes it to Z...'\n\nThat linear walkthrough exposes the gap. 'Wait — I assumed Z receives a string, but Y actually returns an array.' You don't need a duck. A colleague, a notepad, or even talking to yourself works. The point is to switch from scattered internal thinking to structured external explanation.",
          },
        ],
      },
      {
        label: "Communication",
        color: "pink",
        entries: [
          {
            slug: "write-it-down",
            title: "Write it down, even if no one asks",
            body: "Clear written communication — in PRs, Slack messages, or docs — saves everyone time. When you explain your reasoning upfront, people don't need to guess, and decisions are easier to revisit months later.",
            expanded: "I started writing detailed PR descriptions not because anyone asked me to, but because I got tired of answering the same questions in review. 'Why did you use this approach?' 'What does this change affect?' 'Did you consider X?'\n\nNow every PR I open includes: what changed, why it changed, how to test it, and anything the reviewer should pay attention to. It takes five extra minutes to write and saves twenty minutes of back-and-forth.\n\nThe same applies to Slack messages, decision docs, and meeting notes. Writing things down creates a searchable record that your future self and your teammates can reference. 'As discussed in the PR' is much more useful than 'I think someone mentioned it in a meeting once.' Clear writing is a multiplier for every team you're part of.",
          },
          {
            slug: "explain-things-simply",
            title: "Learn to explain technical things simply",
            body: "Being able to translate complex technical concepts into plain language is one of the most valuable skills an engineer can have. Whether it's talking to stakeholders, writing documentation, or onboarding a teammate, clarity wins every time.",
            expanded: "Engineers love precision. We want to use the exact right term, cover every edge case, and qualify every statement. But when you're explaining something to a non-technical stakeholder, that precision becomes noise.\n\nI've learned to start with the 'so what.' Not 'we're migrating from REST to GraphQL with a federated gateway' but 'we're changing how our systems talk to each other so the app loads faster and we can build features more easily.' The technical details matter, but they're for follow-up questions, not the opening line.\n\nThis skill also helps with onboarding. When a new team member asks how something works, the best answer isn't a 20-minute deep dive into implementation details. It's a simple mental model they can build on: 'Think of it like X, but with Y.' Clarity scales better than complexity.",
          },
        ],
      },
      {
        label: "Continuous Learning",
        color: "cyan",
        entries: [
          {
            slug: "learning-never-stops",
            title: "Learning never stops being part of the job",
            body: "Technology moves fast. Frameworks change, best practices evolve, and new tools appear constantly. Treating learning as an ongoing part of your workday — not just something you did in university — is what keeps you relevant and effective.",
            expanded: "When I graduated, I thought I was 'done learning' and ready to just apply what I knew. That illusion lasted about two weeks into my first job. The tech stack was different, the patterns were unfamiliar, and the tools I'd learned in university were already a version behind.\n\nNow I set aside time every week to learn something — even if it's small. A new React hook pattern, a testing technique, or just reading through a well-written open source repo. It doesn't need to be formal. The habit is what matters.\n\nThe engineers who plateau are the ones who stop being curious. The ones who keep growing treat every codebase as a classroom, every PR review as a lesson, and every new tool as an opportunity. The industry rewards people who keep learning, and it's one of the things that makes this career genuinely enjoyable.",
          },
          {
            slug: "teach-to-solidify",
            title: "Teach to solidify your understanding",
            body: "Nothing reveals gaps in your knowledge faster than trying to explain a concept to someone else. Writing notes, mentoring juniors, or creating learning resources forces you to understand things deeply rather than superficially.",
            expanded: "I thought I understood JavaScript closures until I tried to explain them to a junior developer. Midway through my explanation, I realised I was hand-waving through the parts I didn't fully grasp. That moment of 'wait, why does that actually work?' sent me back to the fundamentals.\n\nTeaching forces you to fill the gaps you didn't know existed. When you're just using a concept, you can get by with pattern matching — 'I know this works because I've seen it before.' When you're teaching it, you need to understand why it works, when it breaks, and what the alternatives are.\n\nThis is why I built this portfolio's learning section. It's not just for visitors — it's for me. Structuring topics, writing outlines, and thinking about how to present them has deepened my own understanding more than any course or tutorial.",
          },
        ],
      },
    ],
  },

  resources: {
    heading: "Resources & Toolkit",
    pageTitle: "Resources | The Coding Explorer",
    visitButton: "Visit",
    intro:
      "A curated collection of books, tools, courses, and communities that have helped me grow as a developer. If you're starting out or looking to level up, these are the resources I'd genuinely recommend.",
    categories: [
      {
        label: "Books",
        color: "purple",
        entries: [
          {
            title: "Clean Code — Robert C. Martin",
            body: "The book that changed how I think about naming, functions, and code structure. Some opinions in it are strong, but the core message — that code should be written for humans first — is something I carry into every PR.",
            link: "https://www.amazon.co.uk/Clean-Code-Handbook-Software-Craftsmanship/dp/0132350882",
          },
          {
            title: "The Pragmatic Programmer — Hunt & Thomas",
            body: "Full of practical, timeless advice on being a better developer. Concepts like DRY, orthogonality, and tracer bullets gave me frameworks for thinking about software that I still use daily.",
            link: "https://www.amazon.co.uk/Pragmatic-Programmer-journey-mastery-Anniversary/dp/0135957052",
          },
          {
            title: "You Don't Know JS — Kyle Simpson",
            body: "This series took my JavaScript from 'it works' to 'I understand why it works.' Closures, prototypes, async patterns — it covers the parts of JS that most tutorials skip over.",
            link: "https://github.com/getify/You-Dont-Know-JS",
          },
          {
            title: "Designing Data-Intensive Applications — Martin Kleppmann",
            body: "The best resource I've found for understanding how data systems work under the hood. Databases, replication, partitioning, and distributed systems explained clearly and thoroughly.",
            link: "https://www.amazon.co.uk/Designing-Data-Intensive-Applications-Reliable-Maintainable/dp/1449373321",
          },
        ],
      },
      {
        label: "Tools",
        color: "teal",
        entries: [
          {
            title: "VS Code",
            body: "My daily driver. With extensions like ESLint, Prettier, GitLens, and the React Native debugger, it handles everything from frontend to mobile development. The integrated terminal and source control make it hard to switch to anything else.",
            link: "https://code.visualstudio.com/",
          },
          {
            title: "Git & GitHub",
            body: "Version control is non-negotiable. Beyond basic commits, learning interactive rebase, cherry-pick, and bisect has saved me countless hours debugging and managing releases.",
            link: "https://github.com/",
          },
          {
            title: "Figma",
            body: "Even as a developer, having basic Figma skills helps. Being able to inspect designs, understand spacing and typography tokens, and communicate with designers in their language bridges the gap between design and code.",
            link: "https://www.figma.com/",
          },
          {
            title: "Postman",
            body: "Essential for API development and debugging. Being able to quickly test endpoints, save collections, and share them with teammates speeds up backend work significantly.",
            link: "https://www.postman.com/",
          },
        ],
      },
      {
        label: "Courses & Platforms",
        color: "blue",
        entries: [
          {
            title: "freeCodeCamp",
            body: "Where I first started learning web development properly. The structured curriculum and hands-on projects make it one of the best free resources for anyone starting their coding journey.",
            link: "https://www.freecodecamp.org/",
          },
          {
            title: "Udemy",
            body: "Great for deep dives into specific technologies. Courses on React, React Native, and TypeScript gave me the practical foundation I needed before jumping into production codebases.",
            link: "https://www.udemy.com/",
          },
          {
            title: "Frontend Masters",
            body: "Higher-quality, more advanced content taught by industry experts. Their courses on JavaScript fundamentals, React patterns, and Node.js are some of the best I've taken.",
            link: "https://frontendmasters.com/",
          },
          {
            title: "LeetCode",
            body: "Not just for interview prep. Regular practice with data structures and algorithms keeps problem-solving skills sharp and builds the kind of thinking that makes you a better engineer day-to-day.",
            link: "https://leetcode.com/",
          },
        ],
      },
      {
        label: "Communities",
        color: "orange",
        entries: [
          {
            title: "Stack Overflow",
            body: "Still the single most useful resource for debugging specific issues. Learning to search effectively and read answers critically is a skill in itself. Contributing answers back is a great way to solidify your own understanding.",
            link: "https://stackoverflow.com/",
          },
          {
            title: "GitHub Open Source",
            body: "Reading other people's code on GitHub — especially well-maintained open source projects — is one of the best ways to learn patterns, conventions, and how real production code is structured.",
            link: "https://github.com/explore",
          },
          {
            title: "Dev.to",
            body: "A great platform for reading developer blogs and staying current with trends. Following developers who write about their experiences gives you perspective beyond just documentation and tutorials.",
            link: "https://dev.to/",
          },
          {
            title: "Reactiflux (Discord)",
            body: "One of the largest React communities. A great place to ask questions, share wins, and learn from others' experiences with React, React Native, and the broader JavaScript ecosystem in real time.",
            link: "https://www.reactiflux.com/",
          },
        ],
      },
      {
        label: "AI",
        color: "cyan",
        entries: [
          {
            title: "ChatGPT",
            body: "A versatile AI assistant for brainstorming, drafting documentation, explaining complex concepts, and rubber-ducking code problems. It's become part of my daily workflow for getting unstuck faster.",
            link: "https://chat.openai.com/",
          },
          {
            title: "Claude",
            body: "My go-to for longer, more nuanced conversations about code architecture, debugging, and writing. Excels at understanding large contexts and giving thoughtful, well-structured responses.",
            link: "https://claude.ai/",
          },
          {
            title: "GitHub Copilot",
            body: "AI-powered code completion built right into VS Code. It won't write your app for you, but it speeds up boilerplate, suggests test cases, and helps you stay in flow by reducing context-switching.",
            link: "https://github.com/features/copilot",
          },
          {
            title: "Cursor",
            body: "An AI-first code editor built on top of VS Code. Combines inline code generation, chat, and codebase-aware suggestions into one tool. Great for larger refactors and exploring unfamiliar codebases.",
            link: "https://www.cursor.com/",
          },
          {
            title: "v0 by Vercel",
            body: "An AI tool for generating UI components from text prompts. Useful for quickly prototyping layouts and getting a starting point for React components that you can then refine by hand.",
            link: "https://v0.dev/",
          },
          {
            title: "Perplexity AI",
            body: "An AI-powered search engine that gives direct, sourced answers instead of a list of links. Perfect for quick technical lookups where you want a clear explanation rather than sifting through results.",
            link: "https://www.perplexity.ai/",
          },
        ],
      },
      {
        label: "Frameworks",
        color: "red",
        entries: [
          {
            title: "React",
            body: "The library that powers most of my front-end work. Component-based architecture, hooks, and the massive ecosystem make it my go-to for building interactive UIs. Understanding React deeply has been the single biggest lever in my career.",
            link: "https://react.dev/",
          },
          {
            title: "React Native",
            body: "Building cross-platform mobile apps with the same mental model as React. Working with it professionally at DailyPay taught me the nuances of mobile development — navigation, native modules, and platform-specific behavior.",
            link: "https://reactnative.dev/",
          },
          {
            title: "Next.js",
            body: "The React framework for production. Server-side rendering, static generation, API routes, and file-based routing out of the box. It bridges the gap between a simple React app and a full-stack, production-ready application.",
            link: "https://nextjs.org/",
          },
          {
            title: "Express.js",
            body: "The go-to Node.js framework for building REST APIs. Minimal, unopinionated, and fast to set up. It taught me the fundamentals of server-side routing, middleware, and request handling.",
            link: "https://expressjs.com/",
          },
          {
            title: "Chakra UI",
            body: "An accessible, composable React component library that powers this very site. Its responsive style props, dark mode support, and sensible defaults let me focus on building features instead of fighting CSS.",
            link: "https://chakra-ui.com/",
          },
          {
            title: "Jest & React Testing Library",
            body: "The testing combo I rely on for every React project. Jest handles the test runner and assertions, while Testing Library encourages testing components the way users interact with them — not implementation details.",
            link: "https://testing-library.com/docs/react-testing-library/intro/",
          },
        ],
      },
    ],
  },
};

export default t;
